import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
  TableRow, TableCell, Tooltip, Typography, Box,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DeleteOnePiiButton from './DeleteOnePiiButton';
import UploadToEMRButton from '../integrations/UploadToEMRButton';
import CopyReportButton from './CopyReportButton';
import CopyTraceButton from './CopyTraceButton';
import ResendSMSButton from './ResendSMSButton';
import DownloadReportAsPdfButton from './DownloadReportAsPdfButton';
import DownloadMedicationPdfButton from './DownloadMedicationPdfButton';
import PrintReportButton from './PrintReportButton';

function IntakeTableRow({
  row, header,
  activeSiteId, integrationSettings,
  isNarrowScreen, canReviewIntake, canViewTraces,
  setSuccessMessage, setErrorMessage,
  showAppointmentField, customLogoPath,
  siteName, generateMedsCheckPmrPdf,
}) {
  const navigate = useNavigate();
  const hasIntegrationFailed = (intakeRowData) => !intakeRowData.integrationStatus && intakeRowData.integrationType && intakeRowData.intakeStatus === 'completed';

  const mapIntakeStatus = (status) => {
    // Maps the intake status code from backend to a human-readable format.
    const intakeStatusMap = {
      opened: 'Opened',
      inProgress: 'In Progress',
      idle: isNarrowScreen ? 'Partial' : 'Partially completed',
      consentDeclined: isNarrowScreen ? 'Declined' : 'Consent Declined',
      completed: 'Completed',
      created: 'Created',
    };

    const commonStyle = {
      borderRadius: '16px',
      padding: '4px 12px',
      fontSize: '0.75rem',
      fontWeight: '500',
      whiteSpace: 'nowrap',
    };

    const statusStylesMap = {
      opened: {
        ...commonStyle, backgroundColor: 'rgba(29, 78, 216, 0.2)', color: 'rgb(15 51 154)',
      },
      inProgress: {
        ...commonStyle, backgroundColor: 'rgba(180, 83, 9, 0.2)', color: '#b45309',
      },
      idle: {
        ...commonStyle, backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#21954C',
      },
      consentDeclined: {
        ...commonStyle, backgroundColor: '#6f6f6f54', color: '#28282899',
      },
      completed: {
        ...commonStyle, backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#15803D',
      },
      created: {
        ...commonStyle, backgroundColor: 'rgba(234, 179, 8, 0.2)', color: '#A16207',
      },
    };
    return {
      readableStatus: intakeStatusMap[status] ?? status,
      style: statusStylesMap[status] ?? { ...commonStyle, backgroundColor: '#6f6f6f54', color: '#28282899' },
    };
  };

  const handleReviewIntake = async () => {
    navigate(`/review/${row.mkey}`);
  };

  const clickableStyle = {
    cursor: 'pointer',
    color: '#0056b3',
    fontWeight: 'bold',
  };

  const isStatusClickable = (status) => ['inProgress', 'idle', 'completed', 'integrationFailed', 'abandoned'].includes(status);

  // Uniquely formats the content of each cell based on the column ID and value.
  const formatCell = (id, value) => {
    switch (id) {
      case 'patientId':
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {isStatusClickable(row.intakeStatus) && canReviewIntake ? (
              <Tooltip title="Review Intake">
                <span
                  style={clickableStyle}
                  onClick={handleReviewIntake}
                  role="button"
                  tabIndex={0}
                  data-testid="patientId"
                  onKeyDown={handleReviewIntake}
                >
                  {value ?? 'Not available'}
                </span>
              </Tooltip>
            ) : (
              <Typography variant="semibold" data-testid="patientId">
                {value ?? 'Not available'}
              </Typography>
            )}
            {row.patientIdNonUnique && (
              <Tooltip title="There is another intake with the same Patient ID. Please ensure you have the right patient.">
                <WarningAmberRoundedIcon color="warning" style={{ marginLeft: '8px' }} />
              </Tooltip>
            )}
          </div>
        );
      case 'createdAt':
        return moment(value).format('MMM DD YYYY, h:mm:ss a');
      case 'intakeStatus': {
        if (integrationSettings.accuroEnabled === 'true' && value === 'integrationFailed') {
          return 'Accuro Integration failed';
        }
        const { readableStatus, style } = mapIntakeStatus(value);

        return (
          <span style={style} data-testid="intakeStatus">
            {readableStatus}
          </span>
        );
      }
      case 'appointmentDate':
        return showAppointmentField && value ? moment(value).format('MMM DD, h:mmA') : null;
      default:
        return value ?? 'Not available';
    }
  };

  const renderOptions = (intakeRow) => (
    <Box display="flex" gap={isNarrowScreen ? 0 : 1}>
      {canReviewIntake && (
        <>
          <CopyReportButton mkey={intakeRow.mkey} activeSiteId={activeSiteId} />
          {canViewTraces && !isNarrowScreen
            && <CopyTraceButton mkey={intakeRow.mkey} activeSiteId={activeSiteId} />}
          {!isNarrowScreen && intakeRow.intakeStatus === 'completed' && (
            <>
              <DownloadReportAsPdfButton
                htmlContent={intakeRow.report}
                mkey={intakeRow.mkey}
                activeSiteId={activeSiteId}
                patientID={intakeRow.patientId}
                onSuccess={setSuccessMessage}
                customLogoPath={customLogoPath}
                siteName={siteName}
              />
              <PrintReportButton
                htmlContent={intakeRow.report}
                mkey={intakeRow.mkey}
                activeSiteId={activeSiteId}
                patientID={intakeRow.patientId}
                customLogoPath={customLogoPath}
                siteName={siteName}
              />
            </>
          )}
          {!isNarrowScreen && generateMedsCheckPmrPdf && (
            <DownloadMedicationPdfButton
              mkey={intakeRow.mkey}
              activeSiteId={activeSiteId}
              patientID={intakeRow.patientId}
              onSuccess={setSuccessMessage}
            />
          )}
          <DeleteOnePiiButton
            mkey={intakeRow.mkey}
            patientId={intakeRow.patientId}
            setSuccessMessage={setSuccessMessage}
            setErrorMessage={setErrorMessage}
            data-testid="trashCanIcon"
          />
        </>
      )}
      {(row.intakeStatus === 'created' || row.intakeStatus === 'inProgress') && (row.phoneNumber) && (
        <ResendSMSButton
          mkey={intakeRow.mkey}
          siteId={activeSiteId}
          phoneNumber={row.phoneNumber}
          setSuccessMessage={setSuccessMessage}
          setErrorMessage={setErrorMessage}
        />
      )}
      {
        (hasIntegrationFailed(row)) && (
          <UploadToEMRButton
            mkey={intakeRow.mkey}
            setSuccessMessage={setSuccessMessage}
            setErrorMessage={setErrorMessage}
          />
        )
      }
    </Box>

  );

  return (
    <TableRow>
      {header.map((column) => (
        <TableCell key={column.id}>
          {column.id === 'options' ? renderOptions(row) : formatCell(column.id, row[column.id])}
        </TableCell>
      ))}
    </TableRow>
  );
}

IntakeTableRow.propTypes = {
  row: PropTypes.shape({
    hxId: PropTypes.string.isRequired,
    mkey: PropTypes.string.isRequired,
    intakeStatus: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    createdBy: PropTypes.string.isRequired,
    patientId: PropTypes.string,
    patientIdNonUnique: PropTypes.bool,
    appointmentDate: PropTypes.string,
    phoneNumber: PropTypes.string,
    integrationStatus: PropTypes.bool,
    integrationType: PropTypes.string,
  }).isRequired,
  header: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  activeSiteId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  integrationSettings: PropTypes.object.isRequired,
  isNarrowScreen: PropTypes.bool.isRequired,
  canReviewIntake: PropTypes.bool.isRequired,
  canViewTraces: PropTypes.bool.isRequired,
  setSuccessMessage: PropTypes.func.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  showAppointmentField: PropTypes.bool.isRequired,
  customLogoPath: PropTypes.string,
  siteName: PropTypes.string,
  generateMedsCheckPmrPdf: PropTypes.bool,
};

IntakeTableRow.defaultProps = {
  customLogoPath: null,
  siteName: null,
  generateMedsCheckPmrPdf: false,
};

export default IntakeTableRow;
